import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import type { PrototypeDto } from '@aiai/shared';
import { rotatePrototypeFeedback, setPrototypeFeedback } from '../api';
import { invalidatePrototype } from '../api/prototypeCache';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

/**
 * Toggle end-user feedback collection for a prototype's live serve
 * container — a one-click shortcut over the prototype's write-only
 * `feedback:write` Integration API token (enable = mint/restore, rotate =
 * rotate, disable = revoke). Enabling/disabling and rotating the token both apply to the
 * running container automatically via a background relabel — there is no
 * redeploy step, so the mutation's own pending state is the only "applying"
 * feedback the user needs.
 */
export function PrototypeFeedbackCard({ prototype }: { prototype: PrototypeDto }) {
  const queryClient = useQueryClient();

  const invalidate = () =>
    invalidatePrototype(queryClient, prototype.id);

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => setPrototypeFeedback(prototype.id, enabled),
    onSuccess: invalidate,
  });

  const rotateMutation = useMutation({
    mutationFn: () => rotatePrototypeFeedback(prototype.id),
    onSuccess: invalidate,
  });

  const actionError = toggleMutation.error ?? rotateMutation.error;

  return (
    <SectionCard title="User feedback">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            id="prototype-feedback-enabled"
            type="checkbox"
            className="checkbox"
            checked={prototype.feedbackEnabled}
            onChange={(e) => toggleMutation.mutate(e.target.checked)}
            disabled={toggleMutation.isPending}
          />
          <label htmlFor="prototype-feedback-enabled" className="text-sm text-ink-muted">
            Let end users submit feedback from the live prototype
          </label>
          {toggleMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
        </div>

        {prototype.feedbackEnabled && (
          <div className="space-y-3 border-t border-line pt-3">
            <p className="text-xs text-ink-muted">
              Ask a loop to &quot;add a feedback button&quot; — the API is documented to
              the agent. The token is a write-only <code>feedback:write</code> Integration API
              token (it can file feedback and read nothing else).
            </p>

            {/* Active env var preview so the operator can confirm injection */}
            <div className="rounded-md bg-surface-raised px-3 py-2.5 space-y-1.5">
              <p className="text-xs font-medium text-ink-muted uppercase tracking-wide">
                Injected into serve container
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
                <span className="text-ink-faint select-none">AIAI_FEEDBACK_TOKEN</span>
                <span className="text-ink truncate">
                  {prototype.feedbackTokenPreview ?? (
                    <span className="text-amber-500">not available</span>
                  )}
                </span>
                <span className="text-ink-faint select-none">AIAI_FEEDBACK_URL</span>
                <span className="text-ink break-all">
                  {prototype.feedbackUrl ?? (
                    <span className="text-amber-500">not configured</span>
                  )}
                </span>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => rotateMutation.mutate()}
                disabled={rotateMutation.isPending}
              >
                {rotateMutation.isPending ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : (
                  <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Rotate token
              </button>
            </div>
          </div>
        )}

        {actionError && (
          <Callout tone="danger">
            {errorMessage(actionError, 'Feedback action failed')}
          </Callout>
        )}
      </div>
    </SectionCard>
  );
}
